import { useMemo, useState } from "react";
import { formatDate, formatDateTime } from "@/lib/dateUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  ChevronUp,
  Home,
  Pencil,
  PlusCircle,
  Save,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { handleAPIError } from "@/lib/errorHandler";

type EntryRow = {
  id: string;
  transactionId?: string;
  vendor_id: string;
  transaction_type: "Credit" | "Debit";
  bank_machine_id: string;
  amount: string;
  description: string;
};

const today = () => new Date().toISOString().slice(0, 10);

const createEntryRow = (): EntryRow => ({
  id: crypto.randomUUID(),
  vendor_id: "",
  transaction_type: "Credit",
  bank_machine_id: "",
  amount: "",
  description: "",
});

const money = (value: unknown) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });


export default function VendorTransactions() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [transactionDate, setTransactionDate] = useState(today());
  const [entries, setEntries] = useState<EntryRow[]>([createEntryRow()]);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterVendor, setFilterVendor] = useState("ALL");
  const [tableSearch, setTableSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [itemsPerPage, setItemsPerPage] = useState<"all" | number>("all");
  const [currentPage, setCurrentPage] = useState(1);

  const apiBase = (import.meta as any)?.env?.VITE_API_URL || "";

  const {
    data: transactions = [],
    isLoading: transactionsLoading,
    error: transactionsError,
  } = useQuery({
    queryKey: ["/api/vendor-transactions", filterFrom, filterTo, filterVendor],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("limit", "500");
      if (filterFrom) params.set("from", filterFrom);
      if (filterTo) params.set("to", filterTo);
      if (filterVendor !== "ALL") params.set("vendor_id", filterVendor);

      const response = await fetch(`${apiBase}/api/vendor-transactions?${params.toString()}`, {
        credentials: "include",
      });
      const result = await response.json();
      if (!response.ok || !result?.ok) {
        throw new Error(result?.error || "Failed to fetch vendor transactions");
      }
      return result.rows || [];
    },
    retry: 1,
    retryDelay: 1000,
  });

  const { data: vendors = [], error: vendorsError } = useQuery({
    queryKey: ["/api/vendors"],
    queryFn: async () => {
      const response = await fetch(`${apiBase}/api/vendors`, { credentials: "include" });
      const result = await response.json();
      if (!response.ok || !result?.ok) throw new Error(result?.error || "Failed to fetch vendors");
      return result.rows || [];
    },
    retry: 1,
    retryDelay: 1000,
  });

  const { data: bankParties = [], error: bankError } = useQuery({
    queryKey: ["/api/business-parties"],
    queryFn: async () => {
      const response = await fetch(`${apiBase}/api/business-parties`, { credentials: "include" });
      const result = await response.json();
      if (!response.ok || !result?.ok) throw new Error(result?.error || "Failed to fetch business parties");
      return (result.rows || []).filter((party: any) => {
        return party.party_type === "Bank" && party.is_active !== false;
      });
    },
    retry: 1,
    retryDelay: 1000,
  });

  const bankOptions = useMemo(
    () =>
      bankParties.map((party: any) => ({
        id: String(party.id || party._id),
        label: party.party_name || party.partyName || "Bank",
      })),
    [bankParties]
  );

  const vendorName = (id: string) =>
    vendors.find((vendor: any) => String(vendor.id) === String(id))?.vendor_name || "Unknown Vendor";

  const bankName = (id: string) => bankOptions.find((bank: any) => bank.id === id)?.label || "";

  const updateEntry = (id: string, patch: Partial<EntryRow>) => {
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  };

  const addEntry = () => setEntries((prev) => [...prev, createEntryRow()]);

  const removeEntry = (id: string) => {
    setEntries((prev) => (prev.length === 1 ? prev : prev.filter((entry) => entry.id !== id)));
  };

  const resetForm = () => {
    setTransactionDate(today());
    setEntries([createEntryRow()]);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const invalid = entries
        .map((entry, index) => {
          const row = index + 1;
          if (!transactionDate) return `Row ${row}: choose date`;
          if (!entry.vendor_id) return `Row ${row}: select vendor`;
          if (!entry.bank_machine_id) return `Row ${row}: select bank`;
          if (!entry.amount || Number(entry.amount) <= 0) return `Row ${row}: enter valid amount`;
          return null;
        })
        .filter(Boolean);

      if (invalid.length > 0) throw new Error(invalid.join("; "));

      const rows = entries.map((entry) => ({
        transaction_date: transactionDate,
        vendor_id: entry.vendor_id,
        transaction_type: entry.transaction_type,
        amount: Number(entry.amount),
        payment_mode: "Bank",
        bank_machine_id: entry.bank_machine_id,
        bank_name_snapshot: bankName(entry.bank_machine_id),
        description: entry.description,
      }));

      if (entries.length === 1 && entries[0].transactionId) {
        const response = await fetch(`${apiBase}/api/vendor-transactions/${entries[0].transactionId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(rows[0]),
        });
        const result = await response.json();
        if (!response.ok || !result?.ok) throw new Error(result?.error || "Failed to update transaction");
        return result;
      }

      const response = await fetch(`${apiBase}/api/vendor-transactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ rows }),
      });
      const result = await response.json();
      if (!response.ok || !result?.ok) throw new Error(result?.error || "Failed to save transactions");
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-transactions"] });
      toast({ title: "Success", description: "Vendor transaction saved" });
      resetForm();
    },
    onError: (error: any) => {
      const errorInfo = handleAPIError(error, "Vendor Transaction");
      toast({ variant: "destructive", title: errorInfo.title, description: errorInfo.description });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        const response = await fetch(`${apiBase}/api/vendor-transactions/${id}`, {
          method: "DELETE",
          credentials: "include",
        });
        const result = await response.json();
        if (!response.ok || !result?.ok) throw new Error(result?.error || "Failed to delete transaction");
      }
    },
    onSuccess: () => {
      setSelectedIds({});
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-transactions"] });
      toast({ title: "Success", description: "Deleted" });
    },
    onError: (error: any) => {
      const errorInfo = handleAPIError(error, "Vendor Transaction");
      toast({ variant: "destructive", title: errorInfo.title, description: errorInfo.description });
    },
  });

  const handleEdit = (row: any) => {
    const rowBankId = row.bankMachineId || row.bank_machine_id || "";
    setTransactionDate(row.transactionDate || row.transaction_date || today());
    setEntries([
      {
        id: crypto.randomUUID(),
        transactionId: row.id,
        vendor_id: row.vendorId || row.vendor_id || "",
        transaction_type: row.transactionType || row.transaction_type || "Credit",
        bank_machine_id: rowBankId,
        amount: String(row.amount || ""),
        description: row.description || "",
      },
    ]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectedRowIds = Object.keys(selectedIds).filter((id) => selectedIds[id]);

  const handleDeleteSelected = () => {
    if (selectedRowIds.length === 0) {
      toast({ title: "No rows selected", description: "Select vendor transaction rows before deleting." });
      return;
    }
    if (!window.confirm(`Delete ${selectedRowIds.length} selected transaction(s)?`)) return;
    deleteMutation.mutate(selectedRowIds);
  };

  const filteredRows = useMemo(() => {
    const needle = tableSearch.trim().toLowerCase();
    if (!needle) return transactions;

    return transactions.filter((row: any) => {
      const vendor = row.vendors?.vendor_name || vendorName(row.vendorId || row.vendor_id);
      const by = row.bankNameSnapshot || row.bank_name_snapshot || row.by || row.paymentMode || row.payment_mode;
      return [
        row.transactionDate,
        row.transaction_date,
        vendor,
        row.transactionType,
        row.transaction_type,
        row.amount,
        by,
        row.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [tableSearch, transactions, vendors]);

  const totalPages = itemsPerPage === "all" ? 1 : Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = itemsPerPage === "all" ? 0 : (safePage - 1) * itemsPerPage;
  const endIndex = itemsPerPage === "all" ? filteredRows.length : startIndex + itemsPerPage;
  const visibleRows = filteredRows.slice(startIndex, endIndex);
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row: any) => selectedIds[row.id]);

  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      visibleRows.forEach((row: any) => {
        next[row.id] = checked;
      });
      return next;
    });
  };

  const error = transactionsError || vendorsError || bankError;

  if (error) {
    return (
      <div className="space-y-4 bg-slate-50 min-h-full">
        <div className="h-11 -mx-6 -mt-6 px-6 flex items-center bg-gradient-to-r from-slate-100 to-white border-b border-slate-100">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Home className="h-4 w-4 text-slate-900" />
            <span className="font-bold text-slate-900">Dashboard</span>
            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
            <span>Vendor Transactions</span>
          </div>
        </div>
        <div className="rounded-lg border border-red-200 bg-white p-8 text-center">
          <p className="font-semibold text-red-600">Error loading vendor transactions.</p>
          <p className="mt-2 text-sm text-slate-500">{(error as any)?.message || "Please try again."}</p>
          <Button
            className="mt-4"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ["/api/vendor-transactions"] });
              queryClient.invalidateQueries({ queryKey: ["/api/vendors"] });
              queryClient.invalidateQueries({ queryKey: ["/api/business-parties"] });
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 bg-slate-50 min-h-full">
      <div className="h-11 -mx-6 -mt-6 px-6 flex items-center bg-gradient-to-r from-slate-100 to-white border-b border-slate-100">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Home className="h-4 w-4 text-slate-900" />
          <span className="font-bold text-slate-900">Dashboard</span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <span>Vendor Transactions</span>
        </div>
      </div>

      <section className="rounded-md bg-[#4b61c9] px-5 py-6 text-white shadow-sm">
        <h2 className="mb-7 inline-block border-b border-white/40 px-0.5 pb-2 text-xl font-bold">
          Vendor Transactions
        </h2>

        <div className="grid grid-cols-1 gap-1 md:grid-cols-[220px_220px]">
          <button
            type="button"
            className="h-9 bg-[#ffd35a] px-4 text-xs font-bold text-slate-900"
            onClick={() => document.getElementById("vendor-transaction-date")?.click()}
          >
            Choose Date
          </button>
          <Input
            id="vendor-transaction-date"
            type="date"
            value={transactionDate}
            onChange={(event) => setTransactionDate(event.target.value)}
            className="h-9 rounded-none border-0 bg-white text-xs text-slate-900"
          />
        </div>

        <div className="mt-9 space-y-4">
          {entries.map((entry, index) => (
            <div
              key={entry.id}
              className={
                index === 0
                  ? "grid grid-cols-1 items-end gap-2 lg:grid-cols-[minmax(240px,1fr)_210px_minmax(180px,1fr)_220px_260px_42px]"
                  : "rounded border border-blue-900/50 bg-blue-700/15 p-3 shadow-sm grid grid-cols-1 items-end gap-2 lg:grid-cols-[minmax(240px,1fr)_210px_minmax(180px,1fr)_220px_260px_42px_32px]"
              }
            >
              <Select value={entry.vendor_id || undefined} onValueChange={(value) => updateEntry(entry.id, { vendor_id: value })}>
                <SelectTrigger className="h-9 rounded bg-white text-xs text-slate-900">
                  <SelectValue placeholder="Vendor*" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor: any) => (
                    <SelectItem key={vendor.id} value={String(vendor.id)}>
                      {vendor.vendor_name || vendor.vendorName || "Unknown Vendor"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex h-9 items-center gap-4 px-1 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="h-4 w-4"
                    checked={entry.transaction_type === "Credit"}
                    onChange={() => updateEntry(entry.id, { transaction_type: "Credit" })}
                  />
                  Credit
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="h-4 w-4"
                    checked={entry.transaction_type === "Debit"}
                    onChange={() => updateEntry(entry.id, { transaction_type: "Debit" })}
                  />
                  Debit
                </label>
              </div>

              <div>
                <span className="mb-1 block text-xs font-semibold">Bank</span>
                <Select
                  value={entry.bank_machine_id || undefined}
                  onValueChange={(value) => updateEntry(entry.id, { bank_machine_id: value })}
                >
                  <SelectTrigger className="h-9 rounded bg-white text-xs text-slate-900">
                    <SelectValue placeholder="-Select Bank-" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankOptions.map((bank: any) => (
                      <SelectItem key={bank.id} value={bank.id}>
                        {bank.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Amount*"
                value={entry.amount}
                onChange={(event) => updateEntry(entry.id, { amount: event.target.value })}
                className="h-9 rounded bg-white text-xs text-slate-900"
              />

              <Input
                placeholder="Description"
                value={entry.description}
                onChange={(event) => updateEntry(entry.id, { description: event.target.value })}
                className="h-9 rounded bg-white text-xs text-slate-900"
              />

              <button
                type="button"
                title="Add row"
                className="flex h-9 items-center justify-center text-white"
                onClick={addEntry}
              >
                <PlusCircle className="h-5 w-5" />
              </button>

              {index > 0 && (
                <button
                  type="button"
                  title="Remove row"
                  className="flex h-9 items-center justify-center text-red-400"
                  onClick={() => removeEntry(entry.id)}
                >
                  <XCircle className="h-5 w-5" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-center">
          <Button
            type="button"
            className="h-9 rounded-full bg-lime-400 px-8 text-xs font-bold text-slate-900 hover:bg-lime-500"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            <Save className="mr-1.5 h-3.5 w-3.5" />
            SAVE
          </Button>
        </div>
      </section>

      <section className="rounded-md border border-slate-300 bg-white px-5 py-5 shadow-sm">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[90px_1fr_80px_1fr_70px_1fr_auto] lg:items-center">
          <span className="text-right text-xs font-bold text-indigo-950">From Date</span>
          <Input
            type="date"
            value={filterFrom}
            onChange={(event) => setFilterFrom(event.target.value)}
            className="h-9 rounded-lg border-slate-300 text-xs"
            placeholder="Filter Date"
          />
          <span className="text-right text-xs font-bold text-indigo-950">To Date</span>
          <Input
            type="date"
            value={filterTo}
            onChange={(event) => setFilterTo(event.target.value)}
            className="h-9 rounded-lg border-slate-300 text-xs"
            placeholder="Filter Date"
          />
          <span className="text-right text-xs font-bold text-indigo-950">Vendor</span>
          <Select value={filterVendor} onValueChange={setFilterVendor}>
            <SelectTrigger className="h-9 rounded-lg border-slate-300 text-xs">
              <SelectValue placeholder="Select Vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Vendors</SelectItem>
              {vendors.map((vendor: any) => (
                <SelectItem key={vendor.id} value={String(vendor.id)}>
                  {vendor.vendor_name || vendor.vendorName || "Unknown Vendor"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            className="h-9 bg-orange-500 px-4 text-xs font-bold hover:bg-orange-600"
            onClick={() => {
              setCurrentPage(1);
              queryClient.invalidateQueries({ queryKey: ["/api/vendor-transactions"] });
            }}
          >
            <Search className="mr-1.5 h-3.5 w-3.5" />
            Search
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-lg">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <Button
              type="button"
              variant="outline"
              className="h-8 border-red-500 px-2 text-xs font-bold text-red-600 hover:text-red-700"
              disabled={deleteMutation.isPending}
              onClick={handleDeleteSelected}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Delete
            </Button>
            <div className="flex items-center gap-2 text-xs text-slate-900">
              <span>Show</span>
              <select
                className="h-8 w-[72px] border border-slate-300 bg-white px-2 text-xs outline-none"
                value={String(itemsPerPage)}
                onChange={(event) => {
                  const value = event.target.value === "all" ? "all" : Number(event.target.value);
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-900">Search:</span>
            <div className="relative">
              <Input
                value={tableSearch}
                onChange={(event) => {
                  setTableSearch(event.target.value);
                  setCurrentPage(1);
                }}
                className="h-8 w-[220px] rounded-lg border-slate-300 pr-9 text-xs"
              />
              <Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[1280px] table-fixed border-collapse text-xs">
            <colgroup>
              <col className="w-[5%]" />
              <col className="w-[4%]" />
              <col className="w-[7%]" />
              <col className="w-[12%]" />
              <col className="w-[13%]" />
              <col className="w-[9%]" />
              <col className="w-[9%]" />
              <col className="w-[10%]" />
              <col className="w-[13%]" />
              <col className="w-[8%]" />
              <col className="w-[20%]" />
            </colgroup>
            <thead>
              <tr className="border-y border-slate-300 text-slate-950">
                <th className="px-3 py-3 text-left">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={(value) => toggleAllVisible(Boolean(value))}
                    className="h-3.5 w-3.5 rounded-none border-slate-400"
                  />
                </th>
                <th className="px-3 py-3 text-left">
                  <ChevronUp className="h-3.5 w-3.5 text-slate-900" />
                </th>
                <th className="px-3 py-3 text-left font-bold">S.No</th>
                <th className="px-3 py-3 text-left font-bold">Date</th>
                <th className="px-3 py-3 text-left font-bold">Vendor</th>
                <th className="px-3 py-3 text-right font-bold">Credit(₹)</th>
                <th className="px-3 py-3 text-right font-bold">Debit(₹)</th>
                <th className="px-3 py-3 text-left font-bold">By</th>
                <th className="px-3 py-3 text-left font-bold">Description</th>
                <th className="px-3 py-3 text-center font-bold">Action</th>
                <th className="px-3 py-3 text-left font-bold">User Log Details</th>
              </tr>
            </thead>
            <tbody>
              {transactionsLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index} className="border-b border-slate-200">
                    {Array.from({ length: 11 }).map((__, cell) => (
                      <td key={cell} className="px-3 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-10 text-center text-slate-500">
                    No transactions found.
                  </td>
                </tr>
              ) : (
                visibleRows.map((row: any, index: number) => {
                  const type = row.transactionType || row.transaction_type;
                  const vendor = row.vendors?.vendor_name || vendorName(row.vendorId || row.vendor_id);
                  const by = row.bankNameSnapshot || row.bank_name_snapshot || row.by || row.paymentMode || row.payment_mode || "-";
                  return (
                    <tr key={row.id} className="border-b border-slate-200 odd:bg-slate-50 even:bg-white">
                      <td className="px-3 py-2.5">
                        <Checkbox
                          checked={Boolean(selectedIds[row.id])}
                          onCheckedChange={(value) =>
                            setSelectedIds((prev) => ({ ...prev, [row.id]: Boolean(value) }))
                          }
                          className="h-3.5 w-3.5 rounded-none border-slate-400"
                        />
                      </td>
                      <td className="px-3 py-2.5" />
                      <td className="px-3 py-2.5 text-slate-900">{startIndex + index + 1}</td>
                      <td className="px-3 py-2.5 text-slate-900">{formatDate(row.transactionDate || row.transaction_date)}</td>
                      <td className="truncate px-3 py-2.5 text-slate-900">{vendor}</td>
                      <td className="px-3 py-2.5 text-right text-slate-900">{type === "Credit" ? money(row.amount) : ""}</td>
                      <td className="px-3 py-2.5 text-right text-slate-900">{type === "Debit" ? money(row.amount) : ""}</td>
                      <td className="truncate px-3 py-2.5 text-slate-900">{by}</td>
                      <td className="truncate px-3 py-2.5 text-slate-900">{row.description || ""}</td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          type="button"
                          className="mr-2 inline-flex text-blue-600 hover:text-blue-700"
                          title="Edit"
                          onClick={() => handleEdit(row)}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex text-red-600 hover:text-red-700"
                          title="Delete"
                          onClick={() => {
                            if (window.confirm("Delete this transaction?")) deleteMutation.mutate([row.id]);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">
                        <span className="font-bold">Created:</span> Super Admin{" "}
                        {row.createdAt ? formatDateTime(row.createdAt) : "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-900 px-1 py-3 text-xs text-slate-900 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing {filteredRows.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, filteredRows.length)} of{" "}
            {filteredRows.length} entries
          </span>
          <div className="flex items-center gap-4 self-end sm:self-auto">
            <button
              type="button"
              className="px-2 text-slate-500 disabled:opacity-30"
              disabled={safePage <= 1 || itemsPerPage === "all"}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              Previous
            </button>
            <span className="min-w-8 border border-slate-400 bg-slate-100 px-3 py-2 text-center">{safePage}</span>
            <button
              type="button"
              className="px-2 text-slate-500 disabled:opacity-30"
              disabled={safePage >= totalPages || itemsPerPage === "all"}
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
